import { type FormEvent, useEffect, useState } from "react";
import { kioskSupabase } from "../../lib/supabase";

type Context = { usuario: { nombre: string; email: string; fecha_vencimiento?: string | null }; plan: { id: string; nombre: string; precio: number; proveedor_recurrente: string; proveedores_recurrentes?: string[] }; permanencia_meses: number | null; flow_pending_id?: string | null };
const providersFor = (context: Context) => context.plan.proveedores_recurrentes?.length
  ? context.plan.proveedores_recurrentes : [context.plan.proveedor_recurrente].filter(Boolean);
const providerName = (provider: string) => ({ mercadopago: "Mercado Pago", flow: "Flow", fintoc: "Fintoc", transbank_oneclick_mall: "Transbank Oneclick" }[provider] || provider);

export default function RenovacionPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [context, setContext] = useState<Context | null>(null);
  const [signedIn, setSignedIn] = useState(false);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [flowCheckId, setFlowCheckId] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const flowError = params.get("flow_error");
    if (flowError) {
      setError(flowError === "tarjeta_no_registrada" || flowError === "tarjeta_rechazada"
        ? "Flow no confirmó el registro de la tarjeta. No se realizó ningún cobro; puedes intentarlo nuevamente."
        : "Flow no pudo confirmar la inscripción. No se realizó ningún cobro; intenta nuevamente.");
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    const returnedId = params.get("flow_subscription_id");
    if (params.get("flow_enrolled") === "1" && returnedId) setFlowCheckId(returnedId);
  }, []);

  const reconcileFlow = async (subscriptionId: string) => {
    const { data, error: invokeError } = await kioskSupabase.functions.invoke("flow-subscription-start", { body: { action: "reconcile", subscription_id: subscriptionId } });
    if (invokeError) return;
    const name = String(data?.member_name || context?.usuario.nombre || "Cliente");
    const memberEmail = String(data?.member_email || context?.usuario.email || "");
    if (data?.activated) {
      setSuccess(`${name} — ${memberEmail}: tu tarjeta quedó asociada para pago recurrente con Flow y tu plan está activo.`);
      setFlowCheckId("");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (data?.enrolled) {
      setSuccess(`${name} — ${memberEmail}: tu tarjeta quedó asociada para pago recurrente con Flow. Estamos confirmando el primer cobro para activar tu plan.`);
      setFlowCheckId(subscriptionId);
      window.history.replaceState({}, "", window.location.pathname);
    }
  };

  const loadContext = async () => {
    setBusy(true); setError("");
    const { data: sessionData } = await kioskSupabase.auth.getSession();
    setSignedIn(Boolean(sessionData.session));
    if (!sessionData.session) { setBusy(false); return; }
    const { data, error: invokeError } = await kioskSupabase.functions.invoke("renewal-enrollment-context");
    if (invokeError || !data?.ok) {
      let detail = String(data?.error || "");
      if (!detail && invokeError?.context) { try { detail = String((await invokeError.context.clone().json())?.error || ""); } catch { /* no JSON */ } }
      setError(detail || "No pudimos cargar tu membresía."); setBusy(false); return;
    }
    const loaded = data as Context; setContext(loaded); if (loaded.flow_pending_id) setFlowCheckId(loaded.flow_pending_id); setBusy(false);
  };
  useEffect(() => { void loadContext(); }, []);
  useEffect(() => {
    if (!flowCheckId || !context) return;
    void reconcileFlow(flowCheckId);
    const timer = window.setInterval(() => void reconcileFlow(flowCheckId), 3000);
    return () => window.clearInterval(timer);
  }, [flowCheckId, context]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (event: FormEvent) => {
    event.preventDefault(); setBusy(true); setError("");
    const { error: loginError } = await kioskSupabase.auth.signInWithPassword({ email: email.trim(), password });
    if (loginError) { setError("El correo o la contraseña no son correctos."); setBusy(false); return; }
    await loadContext();
  };

  const logout = async () => {
    setBusy(true); setError(""); setSuccess("");
    await kioskSupabase.auth.signOut();
    setContext(null); setSignedIn(false); setEmail(""); setPassword("");
    window.history.replaceState({}, "", window.location.pathname);
    setBusy(false);
  };

  const enroll = async (provider: string) => {
    if (!context) return;
    setBusy(true); setError("");
    try {
      const functionName = provider === "flow" ? "flow-subscription-start" : provider === "fintoc" ? "fintoc-subscription-start" : provider === "transbank_oneclick_mall" ? "oneclick-inscription-start" : "mercadopago-subscription-start";
      const { data, error: invokeError } = await kioskSupabase.functions.invoke(functionName, { body: { action: "start", plan_id: context.plan.id, back_url: window.location.href, provider } });
      if (invokeError) {
        let detail = ""; try { detail = String((await invokeError.context?.clone().json())?.error || ""); } catch { /* response was not JSON */ }
        throw new Error(detail || invokeError.message);
      }
      if (provider === "flow" && data?.enrolled) {
        const subscriptionId = String(data?.subscription_id || "");
        setSuccess(`${context.usuario.nombre} — ${context.usuario.email}: tu tarjeta quedó asociada para pago recurrente con Flow. ${data?.activated ? "Tu plan está activo." : "Estamos confirmando el primer cobro para activar tu plan."}`);
        if (subscriptionId && !data?.activated) setFlowCheckId(subscriptionId);
        setBusy(false);
        return;
      }
      const destination = String(data?.init_point || data?.url || "");
      if (!destination) throw new Error(String(data?.error || "El proveedor no entregó el enlace de inscripción."));
      if (provider === "transbank_oneclick_mall" && data?.token) {
        const form = document.createElement("form"); form.method = "POST"; form.action = destination;
        const input = document.createElement("input"); input.type = "hidden"; input.name = "TBK_TOKEN"; input.value = String(data.token);
        form.appendChild(input); document.body.appendChild(form); form.submit(); return;
      }
      window.location.assign(destination);
    } catch (cause) { setError(cause instanceof Error ? cause.message : "No se pudo iniciar la renovación."); setBusy(false); }
  };

  return <main className="min-h-screen bg-[#f5f4f1] p-5 grid place-items-center text-slate-900">
    <section className="relative w-full max-w-md bg-white border rounded-3xl shadow-sm p-7">
      {signedIn && <button type="button" onClick={() => void logout()} disabled={busy} className="absolute right-5 top-5 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50" aria-label="Cerrar sesión"><i className="ri-logout-box-r-line" />Salir</button>}
      <div className="w-16 h-16 mx-auto rounded-full bg-[#e7eee3] text-[#314a25] grid place-items-center text-3xl"><i className="ri-repeat-2-line" /></div>
      <h1 className="text-2xl font-extrabold text-center mt-4">Inscribir pago recurrente</h1>
      <p className="text-sm text-gray-500 text-center mt-2">La tarjeta se registra directamente en el sitio seguro del proveedor.</p>
      {busy && <p className="text-center mt-6"><i className="ri-loader-4-line animate-spin text-2xl" /></p>}
      {!busy && !context && <form onSubmit={login} className="space-y-4 mt-6">
        <label className="block text-sm font-semibold">Email<input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full border rounded-xl px-4 py-3" /></label>
        <label className="block text-sm font-semibold">Contraseña<input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full border rounded-xl px-4 py-3" /></label>
        <button className="w-full rounded-xl bg-[#314a25] text-white font-bold py-3">Continuar</button>
      </form>}
      {!busy && context && <div className="mt-6 space-y-4">
        <div className="rounded-xl border bg-gray-50 p-4 text-sm"><p className="font-bold">{context.usuario.nombre}</p><p>{context.plan.nombre}</p><p className="mt-1">${Number(context.plan.precio).toLocaleString("es-CL")} mensuales · {context.permanencia_meses || "sin"} meses de permanencia</p>{context.usuario.fecha_vencimiento && context.usuario.fecha_vencimiento >= new Date().toLocaleDateString("en-CA", { timeZone: "America/Santiago" }) && <p className="mt-3 rounded-lg bg-emerald-50 p-3 font-semibold text-emerald-800">Hoy solo registrarás tu tarjeta. No realizaremos un nuevo cobro durante tu período vigente.</p>}</div>
        <div className="space-y-2">{providersFor(context).map((provider) => <button key={provider} onClick={() => void enroll(provider)} className="w-full rounded-xl bg-[#314a25] text-white font-bold py-3">Inscribir con {providerName(provider)}</button>)}</div>
      </div>}
      {error && <p className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {success && <p className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">{success}</p>}
    </section>
  </main>;
}
